import moment from 'moment-timezone';
import { assert } from '@ember/debug';
import { isEmpty, isPresent } from '@ember/utils';
import { getBrowserTimeZone, MICROSECONDS_IN_SECOND } from '@dashboard/junk-drawer/utils/time';
import { YearMonthDay, LocalTime, DateTime, DateTimeInterval } from '@square/dashboard-protos/models/squareup/common/time';

/**
 * Transforms a Javascript Date object into a squareup.common.time.DateTime proto object
 *
 * @param {Date} date
 * @returns {squareup.common.time.DateTime}
 */
export function dateToProto(date) {
  const unixDate = moment(date).unix();
  return DateTime.create({
    instantUsec: unixDate * MICROSECONDS_IN_SECOND
  });
}

/**
 * Drops the time portion of the date (and timezone, if a moment object), and returns a YearMonthDay proto.
 * @param {moment|Date} time
 * @returns {YearMonthDay}
 */
export function toYearMonthDay(time) {
  const m = moment(time);
  return new YearMonthDay.Builder().year(m.year()).monthOfYear(m.month() + 1).dayOfMonth(m.date()).build();
}
export function dateFromYearMonthDay({
  year,
  monthOfYear,
  dayOfMonth
}) {
  assert('Contains year, month, and day', isPresent(year) && isPresent(monthOfYear) && isPresent(dayOfMonth));
  return moment({
    month: monthOfYear - 1,
    // Moment months are zero-indexed like JS
    day: dayOfMonth,
    year
  }).toDate();
}

/**
 * Drops the date portion (and timezone, if a moment object), and returns a LocalTime object
 * @param {moment|Date} time
 * @returns {LocalTime}
 */
export function toLocalTime(time) {
  const m = moment(time);
  return new LocalTime.Builder().hourOfDay(m.hour()).minuteOfHour(m.minute()).secondOfMinute(m.second()).millisOfSecond(m.millisecond()).build();
}

/**
 * Converts a moment or js Date into a DateTime with any timezone data dropped.
 * This effectively converts the timestamp into a unix timestamp, in proto form.
 * @param {moment|Date} time
 * @returns {DateTime}
 */
export function toDateTimeWithoutZone(time) {
  const m = moment(time);
  return DateTime.create({
    instantUsec: usecFromMoment(m)
  });
}

/**
 * Gets a DateTime proto, with timezone data of the current browser's timezone.
 * @param {moment|Date} time
 * @returns {DateTime}
 */
export function toDateTimeWithBrowserZone(time) {
  if (moment.isMoment(time) && time.tz()) {
    throw new Error('This moment object has a time zone associated with it. To use its time zone, use toDateTimeWithZone()');
  }
  const m = moment.tz(time, getBrowserTimeZone());
  return toDateTimeWithZone(m);
}

/**
 * Gets a DateTime proto, with the timezone data of the passed in moment object. Incompatible with a Date object,
 * since it only supports the browser time zone. In this case, consider using {@link toDateTimeWithBrowserZone}
 * @param {moment} m must be a moment *with time zone* object
 * @returns {DateTime}
 */
export function toDateTimeWithZone(m) {
  if (!moment.isMoment(m)) {
    throw new Error('Passed object must be a moment object. If you want to use the browser time zone, use toDateTimeWithBrowserZone()');
  }
  if (!m.tz()) {
    throw new Error('Passed moment must have a time zone. If you want to use the browser time zone, use toDateTimeWithBrowserZone()');
  }
  return new DateTime.Builder().instantUsec(usecFromMoment(m)).tzName([m.tz()]).timezoneOffsetMin(m.utcOffset()).build();
}

/**
 * Converts a DateTime with timezone data into a timezone-aware moment object
 * @param {DateTime} dateTime
 * @returns {moment}
 */
export function momentFromDateTimeWithZone(dateTime) {
  if (isEmpty(dateTime.tzName)) {
    throw new Error('DateTime proto must have a tzName');
  }
  const validTzName = dateTime.tzName.find(name => moment().tz(name).tz());
  if (!validTzName) {
    throw new Error(`DateTime proto does not have a known timezone; given time zones: ${JSON.stringify(dateTime.tzName)}`);
  }
  return moment.tz(usecToMs(dateTime.instantUsec), validTzName);
}

/**
 * Converts a DateTime into a moment, without timezone data. If the DateTime does have timezone data,
 * it will be ignored.
 * @param {DateTime} dateTime
 * @returns {moment}
 */
export function momentFromDateTimeWithoutZone(dateTime) {
  return moment(usecToMs(dateTime.instantUsec));
}

/**
 * Creates a DateTimeInterval that represents an approximate unix timestamp/instant range, based upon the two
 * input date objects. Any timezone data, and local time data (i.e. the 3:00pm from the example below)
 * are omitted from the proto.
 *
 * For example, if you pass in two objects of times (without a moment tz) `2017-08-01 3:00pm` and `2017-08-03 9:00am`,
 * this will create a DateTimeInterval that represents the date range of 2017-08-01 to 2017-08-03, in the browser's
 * time zone.
 *
 * If the inputs are **not** a moment tz object, then the proto's unix timestamps will represent the begin / end times
 * of the browser's time zone.
 *
 * The resulting interval will respect daylight savings rules. For example, the range `2017-11-05 - 2017-11-05` in
 * pacific time will be a 25-hour long interval, due to daylight savings ending.
 *
 * @param {Date|moment} beginDate
 * @param {Date|moment} endDate
 * @returns {DateTimeInterval}
 */
export function toDateIntervalWithoutZone(beginDate, endDate) {
  const inclusiveStart = moment(beginDate).startOf('day');
  const exclusiveEnd = moment(endDate).startOf('day').add(1, 'day');
  return DateTimeInterval.create({
    inclusiveStart: toDateTimeWithoutZone(inclusiveStart),
    exclusiveEnd: toDateTimeWithoutZone(exclusiveEnd)
  });
}

/**
 * Converts zoned moments to a DateTimeInterval
 * @param {moment} begin expected to have a timezone (i.e. 'America/Los_Angeles')
 * @param {moment} end expected to have a timezone (i.e. 'America/Los_Angeles')
 * @returns {DateTimeInterval}
 */
export function toDateTimeIntervalWithZone(begin, end) {
  return new DateTimeInterval.Builder().inclusiveStart(toDateTimeWithZone(begin)).exclusiveEnd(toDateTimeWithZone(end)).build();
}

/**
 * Gets microseconds from a moment object
 * @param {moment} m
 * @returns {number}
 */
function usecFromMoment(m) {
  return m.valueOf() * 1000;
}

/**
 * Converts microseconds to milliseconds. Javascript and moment only support milliseconds
 * @param {number} usec
 * @returns {number}
 */
function usecToMs(usec) {
  return usec / 1000;
}